// src/pages/UserManagement.jsx

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  orderBy,
  query,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  LogOut,
  Shield,
  Trash2,
  CheckCircle,
  XCircle,
} from "lucide-react";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const usersPerPage = 10;
  const navigate = useNavigate();

  /* ================= AUTH (ADMIN ONLY) ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/agent/login");
        return;
      }

      const snap = await getDocs(query(collection(db, "users")));
      const me = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .find(
          (u) =>
            u.email?.toLowerCase() === user.email?.toLowerCase()
        );

      if (!me || me.role !== "admin" || me.approved !== true) {
        alert("Admins only.");
        await signOut(auth);
        navigate("/agent/login");
      }
    });

    return unsub;
  }, [navigate]);

  /* ================= LOAD USERS ================= */
  const loadUsers = async () => {
    setLoading(true);
    const q = query(
      collection(db, "users"),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  /* ================= ACTIONS ================= */

  // Cycle role: user → agent → admin → user
  const toggleRole = async (id, role) => {
    const next =
      role === "user" ? "agent" : role === "agent" ? "admin" : "user";

    if (!window.confirm(`Change role to ${next}?`)) return;

    await updateDoc(doc(db, "users", id), {
      role: next,
      approved: next === "admin" ? false : true,
    });

    loadUsers();
  };

  // Approve / revoke admin
  const toggleApproval = async (id, approved) => {
    await updateDoc(doc(db, "users", id), {
      approved: !approved,
    });
    loadUsers();
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await deleteDoc(doc(db, "users", id));
    setUsers((p) => p.filter((u) => u.id !== id));
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/agent/login");
  };

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(users.length / usersPerPage) || 1;
  const pageUsers = users.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-black to-slate-800 text-white p-4 pb-24">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center"
        >
          <h1 className="text-3xl font-extrabold text-sky-400 flex items-center gap-2">
            <Shield /> User Management
          </h1>

          <div className="hidden md:flex gap-3">
            <NavBtn icon={<LayoutDashboard />} label="Dashboard" link="/admin/dashboard" />
            <NavBtn icon={<CreditCard />} label="Txns" link="/admin/transactions" />
            <NavBtn icon={<LogOut />} danger onClick={handleLogout} />
          </div>
        </motion.div>

        {/* TABLE */}
        <motion.div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40">
              <tr>
                <th className="p-3 text-left">Email</th>
                <th className="p-3">Role</th>
                <th className="p-3">Approved</th>
                <th className="p-3">Paid</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              <AnimatePresence>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="p-6 text-center">
                      Loading…
                    </td>
                  </tr>
                ) : (
                  pageUsers.map((u) => (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="p-3">{u.email}</td>

                      <td className="p-3 text-center">
                        <span className="px-3 py-1 rounded-full text-xs bg-sky-500/20 text-sky-400">
                          {u.role}
                        </span>
                      </td>

                      <td className="p-3 text-center">
                        {u.role === "admin" ? (
                          u.approved ? (
                            <CheckCircle className="inline text-green-400" />
                          ) : (
                            <XCircle className="inline text-red-400" />
                          )
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="p-3 text-center">
                        {u.role === "agent"
                          ? u.paid
                            ? "Yes"
                            : "No"
                          : "-"}
                      </td>

                      <td className="p-3 flex justify-end gap-2">
                        <button
                          onClick={() => toggleRole(u.id, u.role)}
                          className="px-2 py-1 bg-yellow-500 rounded text-xs"
                        >
                          Role
                        </button>

                        {u.role === "admin" && (
                          <button
                            onClick={() =>
                              toggleApproval(u.id, u.approved)
                            }
                            className="px-2 py-1 bg-green-600 rounded text-xs"
                          >
                            {u.approved ? "Revoke" : "Approve"}
                          </button>
                        )}

                        <button
                          onClick={() => deleteUser(u.id)}
                          className="px-2 py-1 bg-red-600 rounded text-xs flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </motion.div>

        {/* PAGINATION */}
        <div className="flex justify-center gap-4">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-4 py-2 bg-gray-700 rounded disabled:opacity-40"
          >
            ◀
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-4 py-2 bg-gray-700 rounded disabled:opacity-40"
          >
            ▶
          </button>
        </div>
      </div>

      {/* MOBILE NAV */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-black/90 border-t border-white/10 flex justify-around py-3">
        <MobileNav icon={<LayoutDashboard />} label="Home" link="/admin/dashboard" />
        <MobileNav icon={<Users />} label="Users" />
        <MobileNav icon={<CreditCard />} label="Txns" link="/admin/transactions" />
        <button onClick={handleLogout} className="flex flex-col items-center text-red-400">
          <LogOut />
          <span className="text-xs">Logout</span>
        </button>
      </div>
    </div>
  );
}

/* ================= UI HELPERS ================= */

const NavBtn = ({ icon, label, link, onClick, danger }) => (
  <button
    onClick={() => (link ? (window.location.href = link) : onClick())}
    className={`px-4 py-3 rounded-xl font-bold flex items-center gap-2 ${
      danger ? "bg-red-600" : "bg-sky-600"
    }`}
  >
    {icon}
    {label}
  </button>
);

const MobileNav = ({ icon, label, link }) => (
  <button
    onClick={() => link && (window.location.href = link)}
    className="flex flex-col items-center text-gray-300"
  >
    {icon}
    <span className="text-xs">{label}</span>
  </button>
);
